import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { AppRole } from "@/lib/adminAccess";

/**
 * Kullanıcının rollerini sunucu tarafındaki security-definer `has_role`
 * fonksiyonu ile doğrular. Hata/oturumsuz durumda fail-closed (rol yok).
 */
export const useUserRoles = () => {
  const { user, loading } = useAuth();

  const query = useQuery({
    queryKey: ["user-roles", user?.id],
    queryFn: async (): Promise<AppRole[]> => {
      const checks: AppRole[] = ["admin", "moderator"];
      const results = await Promise.all(
        checks.map(async (role) => {
          const { data, error } = await supabase.rpc("has_role", { _user_id: user!.id, _role: role });
          if (error) throw error;
          return data === true ? role : null;
        }),
      );
      return results.filter((r): r is AppRole => r !== null);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const roles = (query.data ?? []) as AppRole[];

  return {
    roles,
    isAdmin: roles.includes("admin"),
    isModerator: roles.includes("moderator"),
    isLoading: loading || (!!user && query.isLoading),
    error: query.error,
  };
};

export default useUserRoles;
